import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Configure Axios base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://exam.doode.in';

const StudentLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error');
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Check mobile width for responsive layout
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true; // invalid token format
    }
  };

  // Clear localStorage and redirect to login if session expired
  const handleTokenExpiry = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('student');
    localStorage.removeItem('id');
    setMessage('Session expired. Please log in again.');
    setMessageType('error');
    navigate('/student-login');
  };

  // On mount: check for existing token and expiry, clear if expired
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      if (isTokenExpired(token)) {
        console.log('Token expired on component mount.');
        handleTokenExpiry();
      } else {
        // Token valid: set axios default header for all requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const res = await axios.post('/api/student/auth/login', { username, password });

      if (res.data.message === 'Login successful' && res.data.student && res.data.token) {
        console.log('Login successful');
        setMessage('Login successful! Redirecting...');
        setMessageType('success');

        const studentData = res.data.student;
        const token = res.data.token;

        // Save token and student data to localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('student', JSON.stringify(studentData));

        // Save student ID separately
        if (studentData._id) {
          localStorage.setItem('id', studentData._id);
          console.log('Stored student ID (_id):', studentData._id);
        } else if (studentData.id) {
          localStorage.setItem('id', studentData.id);
          console.log('Stored student ID (id):', studentData.id);
        } else {
          console.warn('No ID field found in student data');
          localStorage.removeItem('id'); // remove any stale id
        }

        // Set axios Authorization header globally for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Navigate to student dashboard
        setTimeout(() => {
          navigate('/studentmark-dashboard');
        }, 2000);
      } else {
        setMessage('Unexpected response. Please try again.');
        setMessageType('error');
        console.error('Login response missing token or student:', res.data);
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials.';
      if (err.response?.status === 401 && errorMessage.toLowerCase().includes('password')) {
        setMessage('Incorrect password. Please try again.');
      } else {
        setMessage(errorMessage);
      }
      setMessageType('error');
    }
  };

  const handleCloseMessage = () => {
    setMessage('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        height: '100vh',
        overflow: 'hidden', // Prevent overall page scrolling
      }}
    >
      {/* Left Image Section */}
      <div
        style={{
          flex: '0 0 60%',
          display: isMobile ? 'none' : 'block',
          backgroundImage: 'url("../images/studentloginimg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      {/* Right Registration Section */}
      <div
        style={{
          flex: '0 0 40%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
          padding: '10px',
          overflow: 'hidden', // Ensure no overflow for the section
          position: 'relative',
        }}
      >
        {/* Card Section */}
        <div
          style={{
            width: '90%',
            maxWidth: '400px',
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '16px',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            height: 'auto', // Allow card to adjust height based on content
            maxHeight: '80vh', // Limit max height to 80% of viewport to prevent overflow
            overflow: 'hidden', // Prevent internal scrolling
          }}
        >
          <h3 style={{ textAlign: 'center', marginBottom: '16px', color: 'purple', fontFamily: "'Roboto', sans-serif" }}>
            Student Login
          </h3>
          {/* Message Section - Moved inside card, under heading */}
          {message && (
            <div
              style={{
                backgroundColor: '#F3E8FF',
                color: '#6B21A8',
                fontFamily: "'Roboto', sans-serif",
                padding: '8px',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                maxWidth: '300px',
                width: '100%',
                margin: '0 auto 16px auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: 'slideInFromRight 0.5s ease-out',
                overflow: 'hidden', // Prevent scrolling within the message div
                whiteSpace: 'normal', // Allow text to wrap instead of overflow
                maxHeight: '50px', // Limit height to prevent overflow
              }}
            >
              <span style={{ fontFamily: "'Roboto', sans-serif" }}>{message}</span>
              <FontAwesomeIcon
                icon={faTimes}
                style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#6B21A8',
                  fontFamily: "'Roboto', sans-serif",
                }}
                onClick={handleCloseMessage}
                aria-label="Close message"
              />
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              alignItems: 'center',
              marginTop: '2rem',
              flex: '1', // Allow form to take available space
              overflow: 'hidden', // Prevent form from causing scroll
            }}
          >
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={{
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                transition: '0.3s',
                maxWidth: '300px',
                width: '100%',
                fontFamily: "'Roboto', sans-serif",
              }}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                transition: '0.3s',
                maxWidth: '300px',
                width: '100%',
                fontFamily: "'Roboto', sans-serif",
              }}
            />
            <Link
              to="/student-forgot-password"
              style={{
                textDecoration: 'none',
                color: 'purple',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'text-decoration 0.3s ease',
                maxWidth: '300px',
                width: '100%',
                textAlign: 'right',
                fontFamily: "'Roboto', sans-serif",
              }}
              onMouseOver={(e) => (e.target.style.textDecoration = 'underline')}
              onMouseOut={(e) => (e.target.style.textDecoration = 'none')}
            >
              Forgot Password?
            </Link>
            <button
              type="submit"
              style={{
                padding: '5px 20px',
                backgroundImage:
                  'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: 'purple',
                fontWeight: 'bold',
                fontSize: '14px',
                border: '2px solid transparent',
                borderRadius: '30px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginTop: '12px',
                maxWidth: '300px',
                width: '35%',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                fontFamily: "'Roboto', sans-serif",
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'scale(1.03)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              Login
            </button>
          </form>
          <Link
            to="/register"
            style={{
              textDecoration: 'none',
              color: 'purple',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'text-decoration 0.3s ease',
              maxWidth: '300px',
              width: '100%',
              textAlign: 'right',
              marginRight: '15px',
              display: 'block',
              fontFamily: "'Roboto', sans-serif",
            }}
            onMouseOver={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseOut={(e) => (e.target.style.textDecoration = 'none')}
          >
            <div style={{ textAlign: 'right', marginRight: '10px', marginTop: '16px', fontFamily: "'Roboto', sans-serif" }}>
              <span style={{ fontFamily: "'Roboto', sans-serif" }}>Don't have an account? Register</span>
            </div>
          </Link>
        </div>
      </div>
      <style>
        {`
          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default StudentLogin;